/**
 * PublicBoardList — displays public boards the user is not a member of.
 * Uses the same colored tile grid layout as BoardList for visual consistency.
 * Fetches from GET /v1/boards/public with pagination.
 */

import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../services/api-client';

interface PublicBoardSummary {
  readonly boardId: string;
  readonly title: string;
  readonly color: string;
  readonly visibility: string;
  readonly memberCount: number;
}

interface PublicBoardsResponse {
  readonly boards: PublicBoardSummary[];
  readonly nextCursor: string | null;
}

const DEFAULT_BOARD_COLOR = '#0079bf';

export function PublicBoardList(): React.ReactElement {
  const [boards, setBoards] = useState<PublicBoardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchBoards = useCallback(async (cursor?: string) => {
    setLoading(true);
    try {
      const url = cursor ? `/v1/boards/public?cursor=${encodeURIComponent(cursor)}` : '/v1/boards/public';
      const response = await api.get<PublicBoardsResponse>(url);
      if (cursor) {
        setBoards((prev) => [...prev, ...response.boards]);
      } else {
        setBoards(response.boards);
      }
      setNextCursor(response.nextCursor);
    } catch {
      // Fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  if (!loading && boards.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No public boards available.
      </p>
    );
  }

  return (
    <div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" role="list">
        {boards.map((board) => (
          <li key={board.boardId}>
            <a
              href={`/boards/${board.boardId}`}
              className="block w-full text-left rounded-lg border border-border p-4 min-h-[100px] font-bold text-white shadow-sm hover:brightness-90 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              style={{ backgroundColor: board.color || DEFAULT_BOARD_COLOR }}
              aria-label={`Open public board: ${board.title}`}
            >
              <span className="block text-sm leading-snug break-words">{board.title}</span>
              <span className="block mt-2 text-xs font-normal opacity-75">
                {board.memberCount} member{board.memberCount !== 1 ? 's' : ''} · {board.visibility === 'public-edit' ? 'Can edit' : 'View only'}
              </span>
            </a>
          </li>
        ))}
      </ul>

      {loading && (
        <div className="flex justify-center py-4" role="status" aria-label="Loading public boards">
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      )}

      {nextCursor && !loading && (
        <div className="flex justify-center pt-4">
          <button
            type="button"
            onClick={() => fetchBoards(nextCursor)}
            className="text-sm text-primary hover:underline"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
